import { useState } from 'react'

// ── §85: Responsive Preview Matrix ───────────────────────────────────────────
// Show site at multiple viewport sizes simultaneously

const VIEWPORTS = [
  { label: '320px', width: 320, icon: '📱', name: 'Narrow' },
  { label: '375px', width: 375, icon: '📱', name: 'Mobile' },
  { label: '768px', width: 768, icon: '📟', name: 'Tablet' },
  { label: '1024px', width: 1024, icon: '💻', name: 'Laptop' },
  { label: '1280px', width: 1280, icon: '🖥️', name: 'Desktop' },
  { label: '1920px', width: 1920, icon: '🖥️', name: 'Wide' },
]

interface Props {
  url: string
  siteName?: string
  onClose: () => void
}

export function ResponsivePreviewModal({ url, siteName, onClose }: Props) {
  const [selected, setSelected] = useState<number[]>([375, 768, 1280])
  const [zoom, setZoom] = useState(0.4)
  const [activeOnly, setActiveOnly] = useState<number | null>(null)

  function toggleViewport(w: number) {
    setSelected(prev =>
      prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort((a, b) => a - b)
    )
  }

  const displayed = activeOnly !== null ? [activeOnly] : selected

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9900, display: 'flex', flexDirection: 'column' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', background: 'rgba(0,0,0,0.6)', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>
            📐 Responsive Preview {siteName ? `— ${siteName}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            {VIEWPORTS.map(vp => (
              <button
                key={vp.width}
                onClick={() => toggleViewport(vp.width)}
                title={`${vp.name} ${vp.label}`}
                style={{
                  padding: '3px 9px', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: selected.includes(vp.width) ? '#5b6af0' : 'rgba(255,255,255,0.08)',
                  color: selected.includes(vp.width) ? '#fff' : '#888',
                }}
              >
                {vp.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {activeOnly !== null ? (
            <button onClick={() => setActiveOnly(null)}
              style={{ padding: '4px 10px', background: '#374151', border: 'none', borderRadius: 6, fontSize: 11, color: '#e2e8f0', cursor: 'pointer' }}>
              ← Все viewports
            </button>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#888' }}>Масштаб:</span>
            <input
              type="range" min={0.2} max={1} step={0.05} value={zoom}
              onChange={e => setZoom(Number(e.target.value))}
              style={{ width: 80 }}
            />
            <span style={{ fontSize: 11, color: '#888', minWidth: 30 }}>{Math.round(zoom * 100)}%</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Preview area */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', padding: '24px 20px', display: 'flex', gap: 24, alignItems: 'flex-start', justifyContent: displayed.length === 1 ? 'center' : 'flex-start' }}>
        {displayed.length === 0 ? (
          <div style={{ color: '#888', fontSize: 14, textAlign: 'center', paddingTop: 60, width: '100%' }}>
            Выберите хотя бы один viewport выше
          </div>
        ) : displayed.map(width => {
          const vp = VIEWPORTS.find(v => v.width === width)
          return (
            <div key={width} style={{ flexShrink: 0 }}>
              {/* Label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 12 }}>{vp?.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>{vp?.name}</span>
                <span style={{ fontSize: 11, color: '#888' }}>{width}px</span>
                {activeOnly === null && (
                  <button
                    onClick={() => setActiveOnly(width)}
                    style={{ padding: '1px 7px', background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 4, fontSize: 10, color: '#888', cursor: 'pointer', marginLeft: 4 }}
                  >
                    Развернуть
                  </button>
                )}
              </div>

              {/* Frame */}
              <div style={{
                width: width * zoom,
                height: activeOnly !== null ? '70vh' : Math.min(600, 900) * zoom,
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                background: '#fff',
                position: 'relative',
              }}>
                <iframe
                  src={url}
                  title={`${width}px preview`}
                  style={{
                    width: width,
                    height: activeOnly !== null ? `calc(70vh / ${zoom})` : Math.min(600, 900) / zoom,
                    border: 'none',
                    transformOrigin: '0 0',
                    transform: `scale(${zoom})`,
                    display: 'block',
                  }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 20px', background: 'rgba(0,0,0,0.6)', borderTop: '1px solid rgba(255,255,255,0.06)', fontSize: 11, color: '#666', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
        <span>URL: {url}</span>
        <span>·</span>
        <span>§85 Responsive Test Matrix · {VIEWPORTS.length} viewports доступно</span>
      </div>
    </div>
  )
}
